<template>
    <DCard
        :title="__('customers.business_incidents.connected_orders.title', {numberOfItems: items.length})"
        :main-title-has-slot="true"
        padding
    >
        <template #titleHasSlot>
            <DSwitch
                color="success"
                variant="3d"
                :disabled="isInLoadingState"
                :checked="showCardData"
                @update:checked="showCardSection"
            />
        </template>
        <DRow v-if="showCardData">
            <DCol col="6">
                <section class="orders-table">
                    <div class="table-header">
                        <div>{{ __('customers.business_incidents.connected_orders.order_id') }}</div>
                        <div>{{ __('customers.business_incidents.connected_orders.created_at') }}</div>
                        <div>
                            <DButton
                                v-if="userPermissions.includes('customer.incidents_manage') && incident.status === 'open'"
                                id="add"
                                icon="plus"
                                mini
                                :disabled="isInLoadingState"
                                @click="addOrders"
                            />
                        </div>
                    </div>
                    <main>
                        <div
                            v-for="(item, index) in items"
                            :key="index"
                            class="item-group"
                        >
                            <div class="item-header">
                                <div>
                                    <Link :href="orderRoute(item)">
                                        {{ item.order_number }}
                                    </Link>
                                </div>
                                <div>
                                    {{ setDateFormat(item.created_at) }}
                                </div>
                                <div>
                                    <DButton
                                        v-if="userPermissions.includes('customer.incidents_manage') && incident.status === 'open'"
                                        :id="`delete_order_${item.id}`"
                                        :disabled="isInLoadingState"
                                        icon="trash-can-outline"
                                        color="danger"
                                        mini
                                        @click="showRemoveItem(item)"
                                    />
                                </div>
                            </div>
                        </div>
                    </main>
                </section>
            </DCol>
        </DRow>
        <AddItems
            v-if="showAddOrderModal"
            :show-modal="showAddOrderModal"
            :incident="incident"
            :title="__('customers.business_incidents.connected_orders.modal.add.title')"
            :label="__('customers.business_incidents.connected_orders.modal.add.label')"
            :section="'orders'"
            @close-modal="closeAddModal"
            @confirm-modal="save"
        />
        <RemoveItem
            v-if="showRemoveItemModal"
            :show-modal="showRemoveItemModal"
            :title="__('customers.business_incidents.connected_orders.modal.remove.title')"
            :message="__('customers.business_incidents.connected_orders.modal.remove.message', {order: orderId})"
            @close-modal="closeRemoveModal"
            @confirm-modal="remove"
        />
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import RemoveItem from '~@/Components/Customers/BusinessIncidents/RemoveItem'
import AddItems from '~@/Components/Customers/BusinessIncidents/AddItems'

const props = defineProps({
    items: {
        type: Array,
        default () {
            return []
        }
    },
    incident: {
        type: Object,
        default () {
            return {}
        }
    },
    showCard: {
        type: Boolean,
        default () {
            return true
        }
    }
})

const emit = defineEmits(['addOrders'])

const showCardData = ref(props.showCard)
const showRemoveItemModal = ref(false)
const showAddOrderModal = ref(false)
const orderId = ref(null)
const businessIncidentOrderId = ref(null)
const userPermissions = computed(() => usePage().props.auth.permissions)

function showCardSection (value) {
    showCardData.value = value
}

function orderRoute (item) {
    return route('orders.details', { order: item.order_route_id, client: item.order_route_client })
}

function showRemoveItem (item) {
    businessIncidentOrderId.value = item.id
    orderId.value = item.order_id
    showRemoveItemModal.value = true
}

function addOrders () {
    showAddOrderModal.value = true
}

function closeRemoveModal () {
    businessIncidentOrderId.value = null
    orderId.value = null
    showRemoveItemModal.value = false
}

function remove () {
    router.delete(route('customers.customers.delete-business-incident-order', { businessIncidentOrder: businessIncidentOrderId.value }))
    showRemoveItemModal.value = false
}

function closeAddModal () {
    showAddOrderModal.value = false
}

function save (form) {
    emit('addOrders', form)

    showAddOrderModal.value = false
}

</script>

<style lang="scss" scoped>
.orders-table {
    & .table-header {
        border-bottom: 1px solid rgba(0,0,0,0.14);
        display: grid;
        grid-template-columns: 30% 50% 20%;
        grid-gap: 10px;
        align-items: center;

        & > div {
            padding: 10px;
            font-weight: bold;
        }
    }
    & main {
        & .item-group {
            border-bottom: 1px solid rgba(0,0,0,0.3);
            position: relative;

            & .item-header {
                display: grid;
                grid-template-columns: 30% 50% 20%;
                grid-gap: 10px;
                background: #fff;
                border-bottom: 1px solid rgba(0,0,0,0.14);
                position: sticky;
                top: -25px;
                width: 100%;
                z-index: 100;

                & > div {
                    padding: 10px;
                    align-self: center;
                }
            }
        }
    }
}
</style>
